import { TabScreenContainer } from '@/components/TabScreenContainer';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useIsWeb } from '@/hooks/useIsWeb';
import { Button } from '@components/Button/Button';
import { ScreenHeader } from '@components/ScreenHeader';
import ToggleButton from '@components/ToggleButton/ToggleButton';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useReportFiltersPanel } from '@hooks/useReportFiltersPanel';
import { useReportSelection } from '@hooks/useReportSelection';
import { useReportsCsvExport } from '@hooks/useReportsCsvExport/useReportsCsvExport.hook';
import BoardReportsScreen from '@screens/Board/BoardReportScreen';
import MapScreen from '@screens/Board/MapScreen';
import { REPORTS_DISPLAYS } from '@screens/Reports/reportsScreen.constants';
import { ReportsDisplayType } from '@screens/Reports/types/reportsScreen.type';
import { useState } from 'react';
import { useTheme, XStack, YStack } from 'tamagui';

export default function BoardScreen(): React.ReactNode {
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const theme = useTheme();
  const [display, setDisplay] = useState<ReportsDisplayType>(
    ReportsDisplayType.map,
  );
  const { selectedReportIds, selectedReports, handleSelectionChange } =
    useReportSelection();
  const { exportCsv, canExport } = useReportsCsvExport({
    reports: selectedReports,
    filePrefix: 'reports',
  });
  const isWeb = useIsWeb();
  const exportIconColor = theme.color?.val;
  const { appliedFilters, filterButton, filtersPanel } = useReportFiltersPanel({
    isSmallScreen,
    buttonSize: '$3',
  });

  const tabs = (
    <ToggleButton
      value={display}
      options={REPORTS_DISPLAYS}
      onPress={(value) => setDisplay(value as ReportsDisplayType)}
      rounded={isSmallScreen}
      bordered={!isSmallScreen}
    />
  );

  const exportButton =
    display === ReportsDisplayType.list && isWeb && !isSmallScreen ? (
      <Button
        variant="outlined"
        title={t('reports.exportCsv')}
        onPress={exportCsv}
        icon={
          <Ionicons name="download-outline" size={18} color={exportIconColor} />
        }
        disabled={!canExport}
        accessibilityLabel={t('reports.exportCsv')}
      />
    ) : null;

  const headerActions = isSmallScreen ? (
    <XStack>{filterButton}</XStack>
  ) : (
    <XStack gap="$2" alignItems="center">
      {tabs}
      {exportButton}
      {filterButton}
    </XStack>
  );

  return (
    <TabScreenContainer>
      <ScreenHeader title={t('board.title')} actions={headerActions} />
      <XStack flex={1} gap="$4">
        <YStack flex={1}>
          {display === ReportsDisplayType.map ? (
            <MapScreen filters={appliedFilters} />
          ) : (
            <BoardReportsScreen
              filters={appliedFilters}
              selectedReportIds={selectedReportIds}
              onSelectionChange={handleSelectionChange}
            />
          )}
        </YStack>
        {!isSmallScreen && filtersPanel}
      </XStack>
      {isSmallScreen && filtersPanel}
      {isSmallScreen && (
        <XStack
          position="absolute"
          bottom={20}
          left={16}
          right={16}
          justifyContent="center"
          zIndex={99}
        >
          {tabs}
        </XStack>
      )}
    </TabScreenContainer>
  );
}
